"use client";

import { useEffect, useSyncExternalStore } from "react";
import Script from "next/script";
import { GoogleTagManager } from "@next/third-parties/google";

// Vendor IDs + gating config come from env, never hardcoded: a literal ID
// would follow every clone of this site and fire the wrong container.
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const CF_BEACON_TOKEN = process.env.NEXT_PUBLIC_CF_BEACON_TOKEN;
const PRODUCTION_HOSTNAME = process.env.NEXT_PUBLIC_PRODUCTION_HOSTNAME;
const ENABLE_ANALYTICS = process.env.NEXT_PUBLIC_ENABLE_ANALYTICS;

const emptySubscribe = () => () => {};

function isAnalyticsEnabled(): boolean {
  const onProduction =
    !!PRODUCTION_HOSTNAME && window.location.hostname === PRODUCTION_HOSTNAME;
  return onProduction || ENABLE_ANALYTICS === "true";
}

/**
 * Loads the analytics vendors (GTM container, Cloudflare Web Analytics
 * beacon), but only on the real production host. Previews run on
 * *.builder.substrate.nz / *.substrate.wattz.dev and share the production
 * build, so gating on hostname keeps preview traffic out of production
 * analytics. Set NEXT_PUBLIC_ENABLE_ANALYTICS='true' to force it on elsewhere
 * (e.g. staging QA).
 */
export function Analytics() {
  // Hostname is browser-only; useSyncExternalStore reads it hydration-safely
  // (server snapshot false, client snapshot the real gate).
  const enabled = useSyncExternalStore(
    emptySubscribe,
    isAnalyticsEnabled,
    () => false,
  );

  useEffect(() => {
    if (!enabled && (GTM_ID || CF_BEACON_TOKEN)) {
      console.info(
        `[analytics] disabled on ${window.location.hostname} (not the production host)`,
      );
    }
  }, [enabled]);

  if (!enabled) return null;
  return (
    <>
      {GTM_ID && <GoogleTagManager gtmId={GTM_ID} />}
      {CF_BEACON_TOKEN && (
        <Script
          src="https://static.cloudflareinsights.com/beacon.min.js"
          data-cf-beacon={JSON.stringify({ token: CF_BEACON_TOKEN })}
          strategy="afterInteractive"
        />
      )}
    </>
  );
}
